import { buildRosterSlots } from './simulation';
import { ARCHETYPES } from '../data/archetypes';

// Builder modifiers — control QB/TE spending independently of archetype
const BUILDER_MODIFIERS = {
  "Elite QB": { pos: "QB", conflicts: ["Punt QB"] },
  "Punt QB":  { pos: "QB", conflicts: ["Elite QB"] },
  "Elite TE": { pos: "TE", conflicts: ["Punt TE"] },
  "Punt TE":  { pos: "TE", conflicts: ["Elite TE"] },
};

// Elite QB modifier: force one of these three only
const ELITE_QBS = ["Josh Allen", "Lamar Jackson", "Joe Burrow"];
// Elite TE modifier: force one of these two only
const ELITE_TES = ["Trey McBride", "Brock Bowers"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Score candidates, take top 3-5, pick one weighted by score
function pickWeighted(candidates, getPlayerVorp) {
  if (candidates.length === 0) return null;

  // Score with ±40% randomization
  const scored = candidates.map(p => ({
    player: p,
    score: getPlayerVorp(p) * (1 + Math.random() * 0.8 - 0.4),
  }));
  scored.sort((a, b) => b.score - a.score);

  // Take top 3-5 and weighted-random pick among them
  const poolSize = Math.min(3 + Math.floor(Math.random() * 3), scored.length);
  const pool = scored.slice(0, poolSize);

  // Weighted random: score as weight (shift so all positive)
  const minScore = Math.min(...pool.map(s => s.score));
  const shift = minScore < 0 ? -minScore + 1 : 0;
  let totalWeight = 0;
  for (const s of pool) totalWeight += s.score + shift;
  if (totalWeight <= 0) return pool[Math.floor(Math.random() * pool.length)].player;

  let roll = Math.random() * totalWeight;
  for (const s of pool) {
    roll -= s.score + shift;
    if (roll <= 0) return s.player;
  }
  return pool[pool.length - 1].player;
}

// Cheapest player with variety — wider pool + jitter (for bench)
function pickCheapest(candidates, getPlayerValue, getPlayerVorp) {
  if (candidates.length === 0) return null;
  let minVal = Infinity;
  for (const p of candidates) {
    const v = getPlayerValue(p);
    if (v < minVal) minVal = v;
  }
  const cheapPool = candidates.filter(p => getPlayerValue(p) <= minVal + 3);
  return pickWeighted(cheapPool, getPlayerVorp);
}

// ---------------------------------------------------------------------------
// Main builder
// ---------------------------------------------------------------------------

function buildSmartTeam(archetype, players, settings, lockedRoster,
                        getPlayerValue, getPlayerProj, getPlayerVorp,
                        modifiers = []) {
  const arch = ARCHETYPES[archetype];
  if (!arch) throw new Error(`Unknown archetype: "${archetype}"`);

  const allSlots = buildRosterSlots(settings.positions);
  const budget = settings.budget || 200;
  const roster = { ...lockedRoster };
  const rostered = new Set();

  let lockedBudget = 0;
  for (const id of Object.keys(lockedRoster)) {
    const p = lockedRoster[id];
    lockedBudget += getPlayerValue(p);
    rostered.add(`${p.name}-${p.pos}`);
  }

  let remaining = budget - lockedBudget;
  let slotsLeft = allSlots.filter(s => !roster[s.id]).length;

  // Max we can spend on the next pick (reserves $1 per remaining empty slot)
  function cap() { return remaining - Math.max(0, slotsLeft - 1); }

  function assign(slot, player) {
    if (!player) return false;
    const v = getPlayerValue(player);
    if (v > cap()) return false;
    roster[slot.id] = player;
    rostered.add(`${player.name}-${player.pos}`);
    remaining -= v;
    slotsLeft--;
    return true;
  }

  // Available players in position(s) within a value range, capped by budget
  function avail(positions, minVal, maxVal) {
    const c = cap();
    const mx = Math.min(maxVal, c);
    if (mx < minVal) return [];
    return players.filter(p => {
      if (!positions.includes(p.pos)) return false;
      if (rostered.has(`${p.name}-${p.pos}`)) return false;
      const v = getPlayerValue(p);
      return v >= minVal && v <= mx;
    });
  }

  // Pick from range with weighted randomization; fallback to any affordable
  function pick(positions, minVal, maxVal) {
    let pool = avail(positions, minVal, maxVal);
    if (pool.length > 0) return pickWeighted(pool, getPlayerVorp);
    const c = cap();
    pool = players.filter(p =>
      positions.includes(p.pos) && !rostered.has(`${p.name}-${p.pos}`) &&
      getPlayerValue(p) >= 1 && getPlayerValue(p) <= c
    );
    return pickWeighted(pool, getPlayerVorp);
  }

  // Pick from named players only; fallback to best at position
  function pickNamed(names, pos) {
    const c = cap();
    const pool = players.filter(p =>
      p.pos === pos && names.includes(p.name) &&
      !rostered.has(`${p.name}-${p.pos}`) && getPlayerValue(p) <= c
    );
    if (pool.length > 0) return pickWeighted(pool, getPlayerVorp);
    return pick([pos], 1, 999);
  }

  // Empty slots for a given position type
  function empty(pos) { return allSlots.filter(s => s.pos === pos && !roster[s.id]); }

  // ==========================================================================
  // QB — controlled by modifiers only (default: mid-tier $8-20)
  // ==========================================================================
  for (const s of empty("QB")) {
    if (modifiers.includes("Elite QB"))     assign(s, pickNamed(ELITE_QBS, "QB"));
    else if (modifiers.includes("Punt QB")) assign(s, pick(["QB"], 1, 5));
    else                                    assign(s, pick(["QB"], 8, 20));
  }

  // ==========================================================================
  // TE — controlled by modifiers only (default: mid-tier $5-15)
  // ==========================================================================
  for (const s of empty("TE")) {
    if (modifiers.includes("Elite TE"))     assign(s, pickNamed(ELITE_TES, "TE"));
    else if (modifiers.includes("Punt TE")) assign(s, pick(["TE"], 1, 3));
    else                                    assign(s, pick(["TE"], 5, 15));
  }

  // ==========================================================================
  // RB / WR / FLEX — controlled by archetype
  // ==========================================================================
  const rbSlots   = empty("RB");
  const wrSlots   = empty("WR");
  const flexSlots = empty("FLEX");

  switch (archetype) {

    // ---- RB Heavy: 3 quality RBs, mid WRs, FLEX must be RB ----
    case "RB Heavy":
      for (const s of rbSlots)   assign(s, pick(["RB"], 25, 65));
      for (const s of wrSlots)   assign(s, pick(["WR"], 8, 25));
      for (const s of flexSlots) assign(s, pick(["RB"], 10, 45));
      break;

    // ---- WR Heavy: 3 quality WRs, mid RBs, FLEX is WR ----
    case "WR Heavy":
      for (const s of wrSlots)   assign(s, pick(["WR"], 20, 50));
      for (const s of rbSlots)   assign(s, pick(["RB"], 15, 30));
      for (const s of flexSlots) assign(s, pick(["WR"], 10, 40));
      break;

    // ---- Balanced: even spread, nobody dominates, FLEX BPA ----
    case "Balanced":
      for (const s of rbSlots)   assign(s, pick(["RB"], 15, 40));
      for (const s of wrSlots)   assign(s, pick(["WR"], 12, 35));
      for (const s of flexSlots) assign(s, pick(["RB", "WR", "TE"], 5, 25));
      break;

    // ---- Stars & Scrubs: pick 2-3 most expensive players, fill rest $1-5 ----
    case "Stars & Scrubs": {
      const starCount = Math.random() < 0.5 ? 2 : 3;

      // All unfilled starter/flex slots (QB/TE may already be filled by modifiers)
      const allOpen = [...empty("QB"), ...rbSlots, ...wrSlots, ...empty("TE"), ...flexSlots, ...empty("SFLEX")];

      // Sort ALL affordable QB/RB/WR/TE players by value descending, pick the top ones
      const c = cap();
      const allAffordable = players.filter(p =>
        ["QB", "RB", "WR", "TE"].includes(p.pos) &&
        !rostered.has(`${p.name}-${p.pos}`) &&
        getPlayerValue(p) >= 1 && getPlayerValue(p) <= c
      );
      allAffordable.sort((a, b) => getPlayerValue(b) - getPlayerValue(a));

      // Take the most expensive 2-3 players with slight randomization
      // Pick from top ~8 most expensive with value-weighting
      const starPool = allAffordable.slice(0, Math.min(8, allAffordable.length));

      for (let i = 0; i < starCount && starPool.length > 0; i++) {
        // Weight by value — most expensive most likely
        let totalVal = 0;
        for (const p of starPool) totalVal += getPlayerValue(p);
        if (totalVal <= 0) break;

        let roll = Math.random() * totalVal;
        let starIdx = 0;
        for (let j = 0; j < starPool.length; j++) {
          roll -= getPlayerValue(starPool[j]);
          if (roll <= 0) { starIdx = j; break; }
        }
        const star = starPool.splice(starIdx, 1)[0];

        // Find best matching slot: exact position first, then FLEX, then SFLEX
        const pos = star.pos;
        let slot = allOpen.find(s => s.pos === pos && !roster[s.id]);
        if (!slot && ["RB", "WR", "TE"].includes(pos))
          slot = allOpen.find(s => s.pos === "FLEX" && !roster[s.id]);
        if (!slot && ["QB", "RB", "WR", "TE"].includes(pos))
          slot = allOpen.find(s => s.pos === "SFLEX" && !roster[s.id]);
        if (slot) assign(slot, star);
      }

      // Fill ALL remaining open slots with $1-5 scrubs
      for (const s of allOpen) {
        if (roster[s.id]) continue;
        const elig = s.pos === "FLEX" ? ["RB", "WR", "TE"]
                   : s.pos === "SFLEX" ? ["QB", "RB", "WR", "TE"]
                   : [s.pos];
        assign(s, pick(elig, 1, 5));
      }
      break;
    }

    // ---- Zero RB: ALL RBs under $10, elite WRs ($35+), FLEX is WR ----
    case "Zero RB":
      for (const s of wrSlots)   assign(s, pick(["WR"], 35, 999));
      for (const s of flexSlots) assign(s, pick(["WR"], 10, 40));
      for (const s of rbSlots)   assign(s, pick(["RB"], 1, 10));
      break;

    // ---- Hero RB: 1 elite RB ($45+), other RBs under $10, quality WRs, FLEX is WR ----
    case "Hero RB": {
      let heroFilled = false;
      for (const s of rbSlots) {
        if (!heroFilled) { assign(s, pick(["RB"], 45, 999)); heroFilled = true; }
        else              assign(s, pick(["RB"], 1, 10));
      }
      for (const s of wrSlots)   assign(s, pick(["WR"], 15, 45));
      for (const s of flexSlots) assign(s, pick(["WR"], 8, 30));
      break;
    }

    // ---- The Hammer: nobody over $30, all mid-tier, max depth ----
    case "The Hammer":
    default:
      for (const s of rbSlots)   assign(s, pick(["RB"], 10, 30));
      for (const s of wrSlots)   assign(s, pick(["WR"], 10, 30));
      for (const s of flexSlots) assign(s, pick(["RB", "WR", "TE"], 5, 25));
      break;
  }

  // ==========================================================================
  // SFLEX — best available skill player
  // ==========================================================================
  for (const s of empty("SFLEX")) assign(s, pick(["QB", "RB", "WR", "TE"], 3, 15));

  // ==========================================================================
  // DEF & K — exactly 1 each at $1-2, no duplicates
  // ==========================================================================
  const defSlots = empty("DEF");
  if (defSlots.length > 0) assign(defSlots[0], pick(["DEF"], 1, 2));

  const kSlots = empty("K");
  if (kSlots.length > 0) assign(kSlots[0], pick(["K"], 1, 2));

  // ==========================================================================
  // BENCH — realistic composition: 0 DEF, 0 K, max 1 QB, rest RB/WR by archetype
  // ==========================================================================
  const benchSlots = empty("BN");
  const benchCount = benchSlots.length;

  // Bench QB: Punt QB → always, Elite QB → never, no modifier → 50/50
  let addBenchQB = false;
  if (modifiers.includes("Punt QB"))       addBenchQB = true;
  else if (modifiers.includes("Elite QB")) addBenchQB = false;
  else                                     addBenchQB = Math.random() < 0.5;

  // Bench position mix depends on archetype (excluding QB slot)
  const skillSlots = benchCount - (addBenchQB ? 1 : 0);
  let benchRBTarget, benchWRTarget;

  switch (archetype) {
    case "WR Heavy":
    case "Zero RB":
      // Starters are WR-heavy → bench needs RB depth
      benchRBTarget = Math.min(skillSlots, 4 + Math.floor(Math.random() * 2));
      benchWRTarget = skillSlots - benchRBTarget;
      break;
    case "RB Heavy":
      // Starters are RB-heavy → bench needs WR depth
      benchWRTarget = Math.min(skillSlots, 4 + Math.floor(Math.random() * 2));
      benchRBTarget = skillSlots - benchWRTarget;
      break;
    case "Hero RB":
      // Non-hero RBs are cheap → bench mostly RBs for depth
      benchRBTarget = Math.min(skillSlots, 4 + Math.floor(Math.random() * 2));
      benchWRTarget = skillSlots - benchRBTarget;
      break;
    default:
      // Balanced, Value Hunter, Stars & Scrubs → even split
      benchRBTarget = 2 + Math.floor(Math.random() * 2);
      benchWRTarget = skillSlots - benchRBTarget;
      break;
  }

  let benchIdx = 0;

  // Backup QB ($1-3)
  if (addBenchQB && benchIdx < benchCount) {
    const qbPool = players.filter(p =>
      p.pos === "QB" && !rostered.has(`${p.name}-${p.pos}`) &&
      getPlayerValue(p) >= 1 && getPlayerValue(p) <= Math.min(3, cap())
    );
    if (qbPool.length > 0) {
      const qb = pickCheapest(qbPool, getPlayerValue, getPlayerVorp);
      if (qb && assign(benchSlots[benchIdx], qb)) benchIdx++;
    }
  }

  // Bench RBs ($1-5)
  for (let i = 0; i < benchRBTarget && benchIdx < benchCount; i++) {
    const pool = players.filter(p =>
      p.pos === "RB" && !rostered.has(`${p.name}-${p.pos}`) &&
      getPlayerValue(p) >= 1 && getPlayerValue(p) <= Math.min(5, cap())
    );
    const rb = pickCheapest(pool, getPlayerValue, getPlayerVorp);
    if (rb && assign(benchSlots[benchIdx], rb)) benchIdx++;
  }

  // Bench WRs ($1-5)
  for (let i = 0; i < benchWRTarget && benchIdx < benchCount; i++) {
    const pool = players.filter(p =>
      p.pos === "WR" && !rostered.has(`${p.name}-${p.pos}`) &&
      getPlayerValue(p) >= 1 && getPlayerValue(p) <= Math.min(5, cap())
    );
    const wr = pickCheapest(pool, getPlayerValue, getPlayerVorp);
    if (wr && assign(benchSlots[benchIdx], wr)) benchIdx++;
  }

  // Fill any remaining bench with cheapest RB/WR
  while (benchIdx < benchCount) {
    const pool = players.filter(p =>
      ["RB", "WR"].includes(p.pos) && !rostered.has(`${p.name}-${p.pos}`) &&
      getPlayerValue(p) >= 1 && getPlayerValue(p) <= Math.min(5, cap())
    );
    const fill = pickCheapest(pool, getPlayerValue, getPlayerVorp);
    if (!fill) break;
    if (assign(benchSlots[benchIdx], fill)) benchIdx++;
    else break;
  }

  // Compute totals
  let totalSpent = 0, totalProj = 0;
  for (const id of Object.keys(roster)) {
    totalSpent += getPlayerValue(roster[id]);
    totalProj  += getPlayerProj(roster[id]);
  }

  return { roster, totalSpent, totalProj, strategyName: archetype };
}

export { buildSmartTeam };
